/**
 * Title page editor. A simple form over the five bookmarked title-page
 * paragraphs; it rewrites only those paragraphs' text on the DOM, leaving the
 * rest of the title page (and the whole document) untouched.
 */
import { useMemo, useState } from "react";
import {
  readTitlePage,
  writeTitlePage,
  TITLE_BOOKMARKS,
  type TitleValues,
} from "../format/titlepage";
import type { DocumentSession } from "./session";

const LABELS: Record<string, string> = {
  Title: "Title",
  Author: "Author",
  Draft: "Draft / date",
  Contact: "Contact",
  Copyright: "Copyright",
};

export interface TitlePageDialogProps {
  session: DocumentSession;
  onClose: () => void;
  onSaved: () => void;
}

export function TitlePageDialog({ session, onClose, onSaved }: TitlePageDialogProps) {
  const original = useMemo(() => readTitlePage(session.dom), [session]);
  const [values, setValues] = useState<TitleValues>(original);

  const save = () => {
    // Write only fields the user actually changed — untouched bookmarked
    // paragraphs (and any run formatting they carry) are left exactly as-is.
    const changed: TitleValues = {};
    for (const field of TITLE_BOOKMARKS) {
      if ((values[field] ?? "") !== (original[field] ?? "")) {
        changed[field] = values[field] ?? "";
      }
    }
    if (Object.keys(changed).length > 0) {
      writeTitlePage(session.dom, changed);
      session.markDirty();
      onSaved();
    }
    onClose();
  };

  return (
    <div className="sp-modal-backdrop" onClick={onClose}>
      <div
        className="sp-modal"
        role="dialog"
        aria-label="Title page"
        onClick={(e) => e.stopPropagation()}
      >
        <h2 className="sp-modal-title">Title Page</h2>
        <div className="sp-form">
          {TITLE_BOOKMARKS.map((field) => (
            <label key={field} className="sp-field">
              <span className="sp-field-label">{LABELS[field]}</span>
              <input
                className="sp-input"
                type="text"
                value={values[field] ?? ""}
                onChange={(e) =>
                  setValues((v) => ({ ...v, [field]: e.target.value }))
                }
              />
            </label>
          ))}
        </div>
        <div className="sp-modal-actions">
          <button className="sp-btn" onClick={onClose}>
            Cancel
          </button>
          <button className="sp-btn sp-btn-primary" onClick={save}>
            Apply
          </button>
        </div>
      </div>
    </div>
  );
}
